import React, { useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { EditableCell } from './EditableCell.component';
import { DeparturesChoiceComponent } from '../create/components/departuresChoice.component';
import { useDomainPath } from '../../auth/auth.context';
import { get } from '../../fetch';

/**
 * Interface pour les props du composant EditableArrivalMenu
 */
interface Props {
  activityId: string;
  activityOverDays: number;
  departureDate: Dayjs;
  arrivalDate?: Dayjs;
  onChange: (date: Dayjs) => void;
  className?: string;
}

/**
 * Menu déroulant pour la sélection d'heure d'arrivée
 * 
 * Ce composant :
 * - Récupère les créneaux d'arrivée disponibles pour une activité et une date
 * - Calcule automatiquement la date d'arrivée basée sur la durée de l'activité
 * - Affiche les créneaux disponibles via DeparturesChoiceComponent
 * 
 * Logique métier :
 * - Date d'arrivée = date de départ + (durée activité - 1 jour)
 * - Récupération des créneaux depuis l'API arrivals
 * - Source backoffice pour les données internes
 * 
 * @param activityId ID de l'activité
 * @param activityOverDays Durée de l'activité en jours
 * @param departureDate Date de départ
 * @param arrivalDate Date d'arrivée (optionnelle)
 * @param onChange Fonction de callback pour la sélection
 * @returns Composant React du menu d'arrivée
 */
export const EditableArrivalMenu: React.FC<Props> = ({
  activityId,
  activityOverDays,
  arrivalDate,
  departureDate,
  onChange,
}) => {
  const dayOfArrival =
    arrivalDate || departureDate.add(activityOverDays - 1, 'day');
  const source: CRM.requestSource = 'backoffice';
  const arrivalsPath = useDomainPath('/arrivals');
  const [dayArrivals, setDayArrivals] = useState<
    CRM.ActivityOccurrence[] | undefined
  >();

  const fetchDayDepartures = useCallback(
    (date: Dayjs) => {
      const dateFormated = date.format('YYYYMMDD');
      get<CRM.ActivityOccurrence[]>(
        `${arrivalsPath}/activities/${activityId}?day=${dateFormated}&source=${source}`,
      ).then(res => {
        setDayArrivals(res);
      });
    },
    [activityId, arrivalsPath],
  );

  useEffect(() => fetchDayDepartures(dayOfArrival), [
    fetchDayDepartures,
    dayOfArrival,
  ]);

  return (
    <DeparturesChoiceComponent
      dayDepartures={dayArrivals}
      value={arrivalDate}
      selectedDay={dayOfArrival}
      onChange={onChange}
    />
  );
};

/**
 * Cellule éditable pour l'affichage et la sélection d'heure d'arrivée
 * 
 * Ce composant :
 * - Affiche l'heure d'arrivée formatée ou "définir" si non définie
 * - Ouvre un menu déroulant pour la sélection d'heure
 * - Ferme automatiquement le menu après sélection
 * 
 * Logique métier :
 * - Utilise EditableCell pour l'interface utilisateur
 * - Délègue la logique de sélection à EditableArrivalMenu
 * - Gestion de l'état d'ouverture/fermeture du menu
 * 
 * @param props Props du composant (activityId, activityOverDays, etc.)
 * @returns Composant React de la cellule d'arrivée éditable
 */
export const EditableArrivalCell: React.FC<Props> = props => {
  const [isHourSelected, setIshourSelected] = useState<boolean>(false);
  return (
    <EditableCell
      content={
        <span>
          {props.arrivalDate
            ? dayjs(props.arrivalDate).format('HH:mm')
            : 'définir'}
        </span>
      }
      className={props.className}
      closeCell={isHourSelected}
      onClosed={()=>{setIshourSelected(false)}}
    >
      <EditableArrivalMenu {...props}
        onChange={(date)=>{props.onChange(date); setIshourSelected(true);}}
        />
    </EditableCell>
  );
};
